import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import { InView } from 'react-intersection-observer';
import CreateForm from '../create-form';
import { combineClassNames, dropDuplicates, reactParserCommonOptions } from '../../../../app/functions';
import spinLoaderIcon from '../../../../assets/icons/spin-loader.svg';
import * as routes from '../../../../constants/routes';
import { forumCreateComment, forumFindAllComments } from '../../../../redux/actions';
import './index.scss';

const MAX_CHARACTERS_FOR_PREVIEW = 500;
const WHITELISTED_HTML_TAGS = ['a', 'br'];
const SCROLL_TRANSITION_DELAY = 200;
const SCROLL_TRANSITION_OPTIONS = { block: 'center', behavior: 'smooth' };
const SNACKBAR_DURATION = 2000;

const ShimmerWrapper = ({ className }) => (
  <div
    className={combineClassNames(className, `${className}_skeleton`)}
    style={{ position: 'relative' }}
  >
    <div className="shimmer-wrapper">
      <div className="shimmer"/>
    </div>
  </div>
);

const PostCard = ({
  postData = {},
  isSelected = false,
  disableSelectOption = false,
  onSelectClick = () => {},
  isSkeleton = false,
}) => {
  const dispatch = useDispatch();

  const { forumConfiguration } = useSelector((store) => store.config.Parameters);
  const translations = useSelector((store) => store.config.Translations);

  const [commentsData, setCommentsData] = useState([]);
  const [isLoadingComments, setIsLoadingComments] = useState(true);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [isProcessingComment, setIsProcessingComment] = useState(false);
  const [wasCommentCreated, setWasCommentCreated] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [displayLinkCopiedSnackbar, setDisplayLinkCopiedSnackbar] = useState(false);

  const beginningRef = useRef(null);
  const commentsRef = useRef(null);
  const formRef = useRef(null);

  const handleLoadingMoreComments = (inView) => inView && !hasReachedEnd && setCurrentPage(currentPage + 1);

  const fetchCommentsData = (refreshExistingData = false) => {
    setIsLoadingComments(true);

    if (refreshExistingData) {
      setCommentsData([]);
      setHasReachedEnd(false);
      setCurrentPage(0);
    }

    dispatch(forumFindAllComments(
      // General Configuration
      postData._id,
      { page: refreshExistingData ? 0 : currentPage },
      forumConfiguration,

      // Handle Success
      (response) => {
        if (response.data.comments.length > 0) {
          setCommentsData(dropDuplicates([
            ...(refreshExistingData ? [] : commentsData),
            ...response.data.comments,
          ], '_id'));
        }

        if (response.data.comments.length < response.data.pageSize) {
          setHasReachedEnd(true);
        }

        setIsLoadingComments(false);
      },

      // Handle Failure
      () => {
        setIsLoadingComments(false);
        setHasReachedEnd(true);
      },
    ));
  };

  const handleExpandClick = () => {
    onSelectClick();

    setTimeout(() => {
      if (!isSelected && beginningRef.current) {
        beginningRef.current.scrollIntoView(SCROLL_TRANSITION_OPTIONS);
      }
    }, SCROLL_TRANSITION_DELAY);
  };

  const handleCommentClick = () => {
    if (!isSelected) onSelectClick();

    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView(SCROLL_TRANSITION_OPTIONS);
      }
    }, SCROLL_TRANSITION_DELAY);
  };

  const handleCopyLinkClick = () => {
    setDisplayLinkCopiedSnackbar(true);

    navigator.clipboard.writeText(
      window.location.origin + routes.forum + routes.forum_routes.view_post + `/${postData._id}`
    );
  };

  const handleFormSubmit = (formData) => {
    setIsProcessingComment(true);

    dispatch(forumCreateComment(
      // General Configuration
      postData._id,
      formData,
      forumConfiguration,

      // Handle Success
      () => {
        fetchCommentsData(true);
        setIsProcessingComment(false);
        setWasCommentCreated(true);
      },

      // Handle Failure
      () => setIsProcessingComment(false),
    ));
  };

  useEffect(() => {
    if (isSelected && commentsData.length === 0) {
      fetchCommentsData();
    }
  }, [isSelected]);

  useEffect(() => {
    if (isSelected) {
      fetchCommentsData();
    }
  }, [currentPage]);

  useEffect(() => {
    if (wasCommentCreated && commentsRef.current) {
      commentsRef.current.scrollIntoView(SCROLL_TRANSITION_OPTIONS);
    }
  }, [wasCommentCreated]);

  useEffect(() => {
    const hideTimeout = setTimeout(
      () => setDisplayLinkCopiedSnackbar(false),
      SNACKBAR_DURATION,
    );

    return () => clearTimeout(hideTimeout);
  }, [displayLinkCopiedSnackbar]);

  return (
    <div
      className={combineClassNames(
        'forum-post-card',
        isSelected && 'forum-post-card_expanded',
        isSkeleton && 'forum-post-card_skeleton',
      )}
    >
      {isSkeleton ? (
        <div className="post-original-poster">
          <ShimmerWrapper className="post-title" />
          <ShimmerWrapper className="post-content" />
          <div className="post-tags">
            <ShimmerWrapper className="tag-item" />
            <ShimmerWrapper className="tag-item" />
          </div>
        </div>
      ) : (
        <>
          <div className="post-original-poster" id={postData._id}>
            <h2 className="post-title" ref={beginningRef}>
              {disableSelectOption ? postData.subject : (
                <button type="button" onClick={onSelectClick} className="post-button">
                  {postData.subject}
                </button>
              )}
            </h2>

            <p
              className={combineClassNames(
                'post-content',
                !isSelected && 'post-content_inactive',
              )}
            >
              {ReactHtmlParser(
                isSelected
                  ? postData.description.replace(/\n/g, '<br />')
                  : (
                    // Some legacy browsers might not support the CSS "ellipsis" property
                    // So we use JS to limit the number of characters for any case
                    postData.description.length > MAX_CHARACTERS_FOR_PREVIEW
                      ? postData.description.substring(0, MAX_CHARACTERS_FOR_PREVIEW) + '...'
                      : postData.description
                  ),
                reactParserCommonOptions({ whitelistedTags: WHITELISTED_HTML_TAGS, openLinksInNewTab: true }),
              )}
            </p>

            <ul className="post-tags">
              <li className="tag-item">
                <span className="tag-item-title">{translations['forum_post_by']} </span>
                {postData.name}
              </li>

              <li className="tag-item">
                <span className="tag-item-title">{translations['forum_post_from']} </span>
                {new Date(postData.createDate).toLocaleDateString()}
              </li>

              {postData.city && (
                <li className="tag-item">
                  <span className="tag-item-title">{translations['forum_post_city']} </span>
                  {postData.city}
                </li>
              )}

              {postData.region && (
                <li className="tag-item">
                  <span className="tag-item-title">{translations['forum_post_region']} </span>
                  {postData.region}
                </li>
              )}

            </ul>
          </div>
        </>
      )}

      {isSkeleton ? (
        <div className="post-comments">
          <ShimmerWrapper className="post-comments-info" />
        </div>
      ) : (
        <div className="post-comments">
          <p className="post-comments-info">
            {!disableSelectOption && (
              <>
                <button
                  type="button"
                  onClick={handleExpandClick}
                  className="info-textual-button bold-button"
                >
                  {isSelected ? translations['forum_unexpand_post'] : translations['forum_expand_post']}
                </button>
                {' · '}
              </>
            )}
            <button
              type="button"
              onClick={handleCommentClick}
              className="info-textual-button"
            >
              {translations['forum_comment_on_post']}
            </button>
            {' · '}
            <button
              type="button"
              onClick={handleCopyLinkClick}
              className="info-textual-button"
            >
              {translations['forum_copy_post_link_button']}

              {displayLinkCopiedSnackbar && (
                <div className="link-was-copied-snackbar">
                  {translations['forum_post_link_was_copied_indicator']}
                </div>
              )}
            </button>
            {' · '}
            {
              postData.commentsCount > 0
                ? `${postData.commentsCount} ${translations['forum_comments_count']}`
                : translations['forum_no_comments_label']
            }
          </p>

          {isSelected && (
            <>
              <div className="post-comments-list" ref={commentsRef}>
                <h3 className="list-title">{translations['forum_comments_list_title']}</h3>

                {commentsData.length > 0 && commentsData.map((comment) => (
                  <div key={comment._id} id={comment._id} className="comment-item">
                    <h4 className="comment-author">
                      {translations['forum_post_by']} {comment.name}
                    </h4>

                    <p className="comment-content">
                      {ReactHtmlParser(comment.description.replace(/\n/g, '<br />'), reactParserCommonOptions({
                        whitelistedTags: WHITELISTED_HTML_TAGS,
                        openLinksInNewTab: true,
                      }))}
                    </p>

                    <p className="comment-date">
                      {translations['forum_post_from']}{' '}
                      {new Date(comment.createDate).toLocaleDateString()}
                    </p>
                  </div>
                ))}

                {isLoadingComments && (
                  <div className="comment-item">
                    <img
                      src={spinLoaderIcon}
                      alt="Loading..."
                      className="comment-loading-icon"
                    />
                  </div>
                )}

                {!isLoadingComments && commentsData.length === 0 && (
                  <div className="comment-item">
                    <p className="no-comments-message">{translations['forum_no_comments_message']}</p>
                  </div>
                )}

                {!isLoadingComments && !hasReachedEnd && (
                  <InView onChange={handleLoadingMoreComments} />
                )}
              </div>

              <div className="post-comments-form" ref={formRef}>
                <h3 className="form-title">{translations['forum_comments_form_title']}</h3>

                {wasCommentCreated ? (
                  <p className="form-success-message">
                    {translations['forum_create_comment_success_message']}
                  </p>
                ) : (
                  <CreateForm
                    isPartialForm
                    isProcessing={isProcessingComment}
                    onSubmit={handleFormSubmit}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PostCard;
